/**
 * SHOPPING WORKSPACE — /google-ads/shopping
 *
 * Same look as the YouTube and Search workspaces (toolbar · stat tiles ·
 * filter bar · campaign → ad group → product ad rows · list / grid), built for
 * Standard Shopping: campaigns show their Merchant Center, feed label and
 * priority; ad groups show their product groups and bids; product ads show
 * Google's review status.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { BarChart3, Info, Layers, Megaphone, Package, Pause, Play, Plus, ShoppingBag, Store } from 'lucide-react';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { AttachCustomerDialog } from '@/components/google-ads/AttachCustomerDialog';
import {
  googleAdsService, GoogleAdsAdGroupRow, GoogleAdsAdRow, GoogleAdsCampaignSummary, ShoppingCampaignMeta, ShoppingProductGroup,
} from '@/services/google-ads.service';
import {
  adGroupAdResourceName, alreadyToasted, approvalOf, biddingLabel, errMsg, formatMicros, newestFirst, Pill, statusOf, Switch, Tag, useYoutubeAuth,
} from '@/components/google-ads/youtube/youtubeShared';
import {
  CampaignCard, CampaignFilters, ChannelTile, ChildRow, ChipLine, ConnectGate, EmptyWorkspace, Skeleton, StatTiles, useCampaignFilters,
  useCampaignToggle, useViewMode, WorkspaceToolbar,
} from '@/components/google-ads/objectives/workspaceChrome';
import { isShoppingCampaign, priorityLabel, ShoppingObjectiveConfig } from './shoppingShared';

type Counts = { byCampaign: Record<string, { adGroups: number; ads: number; assetGroups?: number }>; byAdGroup: Record<string, number> };

// ── ad group detail ──────────────────────────────────────────────────────

function GroupDetail({ accountUid, customerId, adGroupId, currency, parentPaused }: {
  accountUid: string; customerId: string; adGroupId: string; currency: string | null; parentPaused: boolean;
}) {
  const [groups, setGroups] = useState<ShoppingProductGroup[] | null>(null);
  const [ads, setAds] = useState<GoogleAdsAdRow[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const load = useCallback(async () => {
    const [pg, a] = await Promise.all([
      googleAdsService.shopping.productGroups(accountUid, adGroupId).catch(() => [] as ShoppingProductGroup[]),
      googleAdsService.listAds(accountUid, adGroupId).catch(() => [] as GoogleAdsAdRow[]),
    ]);
    setGroups(pg); setAds(a.filter((x) => !!x.adId));
  }, [accountUid, adGroupId]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (ad: GoogleAdsAdRow) => {
    setBusy(ad.adId);
    const rn = adGroupAdResourceName(customerId, ad.adGroupId, ad.adId);
    try {
      if (ad.status === 'ENABLED') await googleAdsService.pauseAd(accountUid, rn); else await googleAdsService.resumeAd(accountUid, rn);
      await load();
    } catch (e) { if (!alreadyToasted(e)) toast.error(`Google refused — ${errMsg(e)}`); } finally { setBusy(null); }
  };

  if (groups === null || ads === null) return <Skeleton count={2} indent="pl-9" />;
  return (
    <div className="py-2 pl-9">
      <div className="mb-2">
        <ChipLine label="Product groups" empty="None — this ad group can't serve until it has one"
          items={groups.map((g) => `${g.negative ? '− ' : ''}${g.label}${g.cpcBidMicros && Number(g.cpcBidMicros) > 0 ? ` · ${formatMicros(g.cpcBidMicros, currency)}` : ''}`)} />
      </div>
      {ads.length === 0 && <div className="py-2 text-xs text-text-muted">No product ad — add one so the group can serve.</div>}
      {ads.map((ad) => {
        const st = ad.status === 'ENABLED' && parentPaused ? { label: 'Parent paused', tone: 'paused' as const } : statusOf(ad.status);
        const approval = approvalOf(ad.approvalStatus, ad.reviewStatus);
        return (
          <div key={ad.adId} className="flex items-center gap-3.5 border-t border-border/40 py-2.5 first:border-t-0">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-success/80 to-success text-white"><ShoppingBag size={15} /></span>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-text-primary">Product ad</div>
              <div className="mt-0.5 truncate text-xs text-text-muted">Built from your feed — image, title and price per product · #{ad.adId}</div>
            </div>
            {approval && <Pill {...approval} className="hidden sm:inline-flex" />}
            <Pill {...st} />
            <Switch checked={ad.status === 'ENABLED'} disabled={busy === ad.adId || ad.status === 'REMOVED'} label="Enable product ad" onCheckedChange={() => toggle(ad)} />
          </div>
        );
      })}
    </div>
  );
}

function AdGroupRows({ accountUid, customerId, campaign, currency, base, adCounts }: {
  accountUid: string; customerId: string; campaign: GoogleAdsCampaignSummary; currency: string | null; base: string; adCounts?: Record<string, number>;
}) {
  const navigate = useNavigate();
  const [groups, setGroups] = useState<GoogleAdsAdGroupRow[] | null>(null);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const load = useCallback(async () => {
    setError('');
    try { setGroups((await googleAdsService.listAdGroups(accountUid, campaign.id)).filter((g) => !!g.id)); } catch (e) { setError(errMsg(e)); }
  }, [accountUid, campaign.id]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (g: GoogleAdsAdGroupRow) => {
    setBusy(g.id);
    try {
      if (g.status === 'ENABLED') await googleAdsService.pauseAdGroup(accountUid, g.id); else await googleAdsService.resumeAdGroup(accountUid, g.id);
      await load();
    } catch (e) { if (!alreadyToasted(e)) toast.error(`Google refused — ${errMsg(e)}`); } finally { setBusy(null); }
  };

  if (error) return <div className="py-2 text-xs text-danger">{error} <button type="button" className="ml-1 underline" onClick={load}>Retry</button></div>;
  if (groups === null) return <Skeleton count={2} indent="pl-8" />;
  const manual = (campaign.biddingStrategyType ?? '').startsWith('MANUAL_CPC');
  return (
    <div className="space-y-1 pl-2">
      {groups.length === 0 && <div className="py-2 text-sm text-text-muted">No ad groups yet — the campaign can't show products without one.</div>}
      {groups.map((g) => {
        const open = openId === g.id;
        const st = statusOf(g.status, g.primaryStatus);
        const cpc = manual ? formatMicros(g.cpcBidMicros, currency) : null;
        const n = adCounts?.[g.id];
        return (
          <ChildRow key={g.id} open={open} onToggle={() => setOpenId(open ? null : g.id)}
            lead={<Layers size={16} className="shrink-0 text-text-muted/70" />}
            title={g.name}
            sub={<>Shopping ad group{cpc ? ` · max CPC ${cpc}` : ''}{n !== undefined ? ` · ${n} product ad${n === 1 ? '' : 's'}` : ''}</>}
            actions={g.status !== 'REMOVED' && (n ?? 0) === 0 && (
              <Button variant="ghost" size="sm" className="h-8 gap-1 px-2.5 text-sm text-text-secondary hover:text-text-primary" onClick={() => navigate(`${base}/create/ad?adgroup_id=${g.id}`)}>
                <Plus size={13} /> Product ad
              </Button>
            )}
            status={<><Pill {...st} /><Switch checked={g.status === 'ENABLED'} disabled={busy === g.id || g.status === 'REMOVED'} label={`Enable ${g.name}`} onCheckedChange={() => toggle(g)} /></>}>
            <GroupDetail accountUid={accountUid} customerId={customerId} adGroupId={g.id} currency={currency}
              parentPaused={campaign.status !== 'ENABLED' || g.status !== 'ENABLED'} />
          </ChildRow>
        );
      })}
    </div>
  );
}

// ── workspace ────────────────────────────────────────────────────────────

export default function ShoppingWorkspace({ cfg }: { cfg: ShoppingObjectiveConfig }) {
  const navigate = useNavigate();
  const { auth, account, accountUid, checkAuth } = useYoutubeAuth();
  const ready = !!(auth?.connected && auth?.customer_selected && accountUid);
  const customerId = account?.customerId ?? '';
  const currency = auth?.currency ?? null;
  const newRoute = `${cfg.base}/create/campaign`;

  const [campaigns, setCampaigns] = useState<GoogleAdsCampaignSummary[] | null>(null);
  const [meta, setMeta] = useState<Record<string, ShoppingCampaignMeta>>({});
  const [counts, setCounts] = useState<Counts | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [view, setView] = useViewMode('gads_shopping_view');

  const load = useCallback(async () => {
    if (!ready) return;
    setListError(null);
    setRefreshing(true);
    try {
      const [list, m] = await Promise.all([
        googleAdsService.listCampaigns(accountUid),
        googleAdsService.shopping.campaignMeta(accountUid).catch(() => ({} as Record<string, ShoppingCampaignMeta>)),
      ]);
      setMeta(m);
      setCampaigns(newestFirst(list.filter(isShoppingCampaign)));
    } catch (e) {
      setListError(errMsg(e, 'Could not load campaigns.'));
      setCampaigns([]);
    } finally { setRefreshing(false); }
    googleAdsService.campaignCounts(accountUid).then(setCounts).catch(() => undefined);
  }, [ready, accountUid]);
  // customerId: re-attaching a different customer on the same account must refetch.
  useEffect(() => { load(); }, [load, customerId]);

  const f = useCampaignFilters(campaigns);
  const { busy, toggle } = useCampaignToggle(accountUid, load);

  const subline = (c: GoogleAdsCampaignSummary) => {
    const m = meta[c.id];
    const cnt = counts?.byCampaign[c.id];
    const parts = [
      biddingLabel(c.biddingStrategyType),
      m?.targetRoas ? `target ROAS ${Math.round(m.targetRoas * 100)}%` : null,
      formatMicros(c.budgetAmountMicros, currency) && `${formatMicros(c.budgetAmountMicros, currency)} / day`,
    ].filter(Boolean);
    return (
      <>
        <span className="truncate">{parts.join(' · ') || 'Shopping'}</span>
        {m?.merchantId && <span className="inline-flex items-center gap-1"><Store size={12} /> MC {m.merchantId}{m.feedLabel ? ` · ${m.feedLabel}` : ''}</span>}
        {cnt && <span className="inline-flex items-center gap-1"><Layers size={12} /> {cnt.adGroups} ad group{cnt.adGroups === 1 ? '' : 's'}</span>}
      </>
    );
  };
  const tags = (c: GoogleAdsCampaignSummary) => {
    const m = meta[c.id];
    if (!m) return undefined;
    return (
      <>
        {m.campaignPriority ? <Tag className="shrink-0">{priorityLabel(m.campaignPriority)} priority</Tag> : null}
      </>
    );
  };

  const merchants = new Set(f.live.map((c) => meta[c.id]?.merchantId).filter(Boolean));
  const stats = [
    { label: 'Campaigns', value: f.live.length, Icon: Megaphone },
    { label: 'Enabled', value: f.live.filter((c) => c.status === 'ENABLED').length, Icon: Play },
    { label: 'Paused', value: f.live.filter((c) => c.status === 'PAUSED').length, Icon: Pause },
    merchants.size
      ? { label: 'Merchant Center feeds', value: merchants.size, Icon: Store }
      : { label: 'Daily budget (enabled)', value: formatMicros(f.live.filter((c) => c.status === 'ENABLED').reduce((n, c) => n + Number(c.budgetAmountMicros || 0), 0), currency) ?? '0', Icon: BarChart3 },
  ];

  return (
    <>
      <Topbar title={cfg.title} breadcrumb={`Google Ads / ${cfg.title}`} description={cfg.subtitle} />

      <div className="gads-workspace w-full px-5 py-4">
        {auth === undefined && <div className="space-y-3"><div className="skeleton h-14 rounded-2xl" /><Skeleton count={3} /></div>}
        {auth !== undefined && !ready && <ConnectGate auth={auth ?? { connected: false }} accountUid={accountUid} onChanged={checkAuth} />}

        {ready && (
          <div className="w-full pb-10">
            <WorkspaceToolbar auth={auth} onCustomer={() => setPickerOpen(true)} onRefresh={load} refreshing={refreshing}
              view={view} onView={setView} onNew={() => navigate(newRoute)} />
            <StatTiles stats={stats} loading={campaigns === null || !!listError} />
            <CampaignFilters f={f} />

            <div className="space-y-3">
              {listError && <Card role="alert" className="border-danger/30 bg-danger-soft p-4 text-sm text-danger">{listError}<button type="button" className="ml-3 underline" onClick={load}>Try again</button></Card>}
              {campaigns === null && !listError && <div className="space-y-3">{[0, 1, 2].map((i) => <div key={i} className="skeleton h-[68px] rounded-2xl" />)}</div>}

              {campaigns !== null && f.live.length === 0 && f.removed.length === 0 && !listError && (
                <EmptyWorkspace icon={ShoppingBag} title="No Shopping campaigns yet" hint={cfg.emptyHint} onNew={() => navigate(newRoute)}
                  points={['Merchant Center feed', 'Photo, title & price from the feed', 'Product groups & bids', 'Starts paused']} />
              )}

              {view === 'list' && f.visible.length > 0 && (
                <div className="gads-campaign-list">
                  {f.visible.map((c) => (
                    <CampaignCard key={c.id} c={c} open={openId === c.id} onToggle={() => setOpenId(openId === c.id ? null : c.id)}
                      tile={<ChannelTile icon={ShoppingBag} tone="success" />} tags={tags(c)} subline={subline(c)} busy={busy === c.id} onStatus={() => toggle(c)}>
                      <AdGroupRows accountUid={accountUid} customerId={customerId} campaign={c} currency={currency} base={cfg.base} adCounts={counts?.byAdGroup} />
                      {c.status !== 'REMOVED' && (
                        <div className="pl-9 pt-3">
                          <Button variant="ghost" size="sm" className="h-8 gap-1.5 px-2.5 text-sm text-text-secondary hover:text-text-primary"
                            onClick={() => navigate(`${cfg.base}/create/adgroup?campaign_id=${c.id}`)}>
                            <Plus size={13} /> New ad group
                          </Button>
                        </div>
                      )}
                    </CampaignCard>
                  ))}
                </div>
              )}

              {view === 'grid' && f.visible.length > 0 && (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 2xl:grid-cols-3">
                  {f.visible.map((c) => {
                    const st = statusOf(c.status, c.primaryStatus);
                    const m = meta[c.id];
                    return (
                      <Card key={c.id} padded={false} className="flex flex-col overflow-hidden">
                        <div className="relative flex aspect-[1.91/1] w-full items-center justify-center gap-2 overflow-hidden bg-gradient-to-br from-success-soft to-surface-alt px-6">
                          {[0, 1, 2].map((i) => (
                            <span key={i} className="flex aspect-[3/4] w-1/4 flex-col overflow-hidden rounded-md border border-border bg-surface shadow-soft">
                              <span className="flex flex-1 items-center justify-center bg-success/10 text-success"><Package size={16} /></span>
                              <span className="space-y-1 p-1.5"><span className="block h-1 w-4/5 rounded bg-border" /><span className="block h-1 w-1/2 rounded bg-border" /></span>
                            </span>
                          ))}
                          <span className="absolute right-2 top-2"><Pill {...st} /></span>
                          {m?.merchantId && <span className="absolute bottom-2 left-2 rounded bg-black/65 px-1.5 py-0.5 text-[10px] font-medium text-white">Merchant Center {m.merchantId}</span>}
                        </div>
                        <div className="flex flex-1 flex-col p-3.5">
                          <span className="truncate text-sm font-semibold text-text-primary">{c.name}</span>
                          <div className="mt-0.5 flex flex-wrap items-center gap-x-3 truncate text-xs text-text-muted">{subline(c)}</div>
                          <div className="mt-3 flex items-center gap-2 border-t border-border/50 pt-3">
                            <Switch checked={c.status === 'ENABLED'} disabled={busy === c.id || c.status === 'REMOVED'} label={`Enable ${c.name}`} onCheckedChange={() => toggle(c)} />
                            <span className="text-xs text-text-muted">{c.status === 'ENABLED' ? 'Enabled' : c.status === 'REMOVED' ? 'Removed' : 'Paused'}</span>
                            <span className="flex-1" />
                            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => { setView('list'); setOpenId(c.id); }}>Open</Button>
                          </div>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              )}

              {campaigns !== null && (f.live.length > 0 || f.removed.length > 0) && f.visible.length === 0 && (
                <Card className="p-8 text-center text-sm text-text-muted">No campaigns match your filters.</Card>
              )}

              {campaigns !== null && !listError && f.live.length > 0 && (
                <p className="flex items-start gap-1.5 px-1 pt-1 text-2xs text-text-muted">
                  <Info size={12} className="mt-px shrink-0" />
                  <span>Every Standard Shopping campaign on this Google Ads account is listed here. Product-level bids beyond "All products" can be split in Google Ads.</span>
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {ready && (
        <AttachCustomerDialog open={pickerOpen} accountUid={accountUid} onClose={() => setPickerOpen(false)}
          onAttached={async () => { setPickerOpen(false); await checkAuth(); }} />
      )}
    </>
  );
}
